import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Globe, Server, Code, Search, ShoppingCart, MessageCircle, ChevronLeft, ChevronRight, Check, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from './ui/card';
import { Button } from './ui/button';
import { webServicesData, contactInfo } from '../data/mock';

const ImageCarousel = ({ images, serviceName }) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(timer);
  }, [images.length]);

  const goToPrevious = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    setCurrentIndex((prev) => (prev + 1) % images.length);
  };

  return (
    <div className="relative h-56 overflow-hidden bg-black/50 group">
      <AnimatePresence mode="wait">
        <motion.img
          key={currentIndex}
          src={images[currentIndex]}
          alt={`${serviceName} - ${currentIndex + 1}`}
          className="w-full h-full object-cover"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
        />
      </AnimatePresence>
      
      {/* Navigation Arrows */}
      <button
        onClick={goToPrevious}
        className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <ChevronLeft className="w-5 h-5" />
      </button>
      <button
        onClick={goToNext}
        className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/50 hover:bg-black/70 text-white p-1.5 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
      >
        <ChevronRight className="w-5 h-5" />
      </button>

      {/* Dots Indicator */}
      <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
        {images.map((_, idx) => (
          <button
            key={idx}
            onClick={(e) => {
              e.stopPropagation();
              setCurrentIndex(idx);
            }}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === currentIndex ? 'bg-blue-500 w-4' : 'bg-white/50 hover:bg-white/70'
            }`}
          />
        ))}
      </div>
    </div>
  );
};

const WebServicesSection = () => {
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  
  const categories = ['Todos', ...new Set(webServicesData.map(s => s.category))];
  
  const filteredServices = selectedCategory === 'Todos' 
    ? webServicesData 
    : webServicesData.filter(s => s.category === selectedCategory);

  const handleConsultar = (service) => {
    const message = `Hola! Me interesa el servicio de ${service.name}. ¿Podrían darme más información?`;
    const whatsappUrl = `https://wa.me/${contactInfo.social.whatsapp}?text=${encodeURIComponent(message)}`;
    window.open(whatsappUrl, '_blank');
  };

  const getServiceIcon = (category) => {
    switch (category) {
      case 'Infraestructura': return Server;
      case 'Desarrollo': return Code;
      case 'Marketing': return Search;
      case 'E-commerce': return ShoppingCart;
      default: return Globe;
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.95 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { type: 'spring', stiffness: 100, damping: 12 }
    }
  };

  return (
    <section className="min-h-screen bg-black pt-24 pb-16">
      <div className="max-w-7xl mx-auto px-4 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600/20 border border-blue-500/30 rounded-full mb-6"
          >
            <Globe className="w-4 h-4 text-blue-400" />
            <span className="text-blue-300 text-sm font-mono tracking-wider uppercase">Páginas Web</span>
          </motion.div>
          <motion.h2 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-4xl lg:text-5xl font-bold text-white mb-4"
          >
            Páginas Web
          </motion.h2>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-gray-400 text-lg max-w-2xl mx-auto"
          >
            Tu presencia digital profesional. Desde el hosting hasta la venta online, te acompañamos en cada paso.
          </motion.p>
        </div>

        {/* Category Filters */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="flex flex-wrap justify-center gap-3 mb-10"
        >
          {categories.map((category) => (
            <button
              key={category}
              onClick={() => setSelectedCategory(category)}
              className={`px-4 py-2 rounded-full text-sm font-mono tracking-wide transition-all
                ${selectedCategory === category 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-white border border-blue-900/30'
                }`}
            >
              {category}
            </button>
          ))}
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="grid grid-cols-1 lg:grid-cols-2 gap-8"
        >
          {filteredServices.map((service) => {
            const IconComponent = getServiceIcon(service.category);
            return (
              <motion.div 
                key={service.id} 
                variants={itemVariants}
                whileHover={{ y: -4 }}
              >
                <Card className={`bg-white/5 border-blue-900/30 hover:border-blue-500/50 transition-all h-full flex flex-col relative overflow-hidden hover:shadow-lg hover:shadow-blue-500/10
                  ${service.featured ? 'ring-1 ring-blue-500/50' : ''}`}
                >
                  {/* Image Carousel */}
                  <ImageCarousel images={service.images} serviceName={service.name} />
                  
                  {/* Category Badge */}
                  <div className="absolute top-3 left-3 z-10">
                    <span className="flex items-center gap-1.5 px-3 py-1 bg-blue-600/90 text-white rounded-full text-xs font-mono backdrop-blur-sm">
                      <IconComponent className="w-3 h-3" />
                      {service.category}
                    </span>
                  </div>
                  
                  {service.featured && (
                    <div className="absolute top-3 right-3 z-10">
                      <span className="px-2 py-1 bg-amber-500 text-black rounded-full text-xs font-bold">
                        ⭐ Destacado
                      </span>
                    </div>
                  )}

                  <CardHeader className="pb-2">
                    <CardTitle className="text-white text-xl flex items-center gap-2">
                      {service.name}
                    </CardTitle>
                    <CardDescription className="text-gray-400 text-sm leading-relaxed">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  
                  <CardContent className="flex-grow">
                    {/* Features List */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-4">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start gap-2">
                          <Check className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                          <span className="text-gray-300 text-xs">{feature}</span>
                        </div>
                      ))}
                    </div>
                    
                    {/* Price */}
                    <div className="text-blue-400 font-bold text-lg">
                      {service.priceText}
                    </div>
                  </CardContent>
                  
                  <CardFooter>
                    <Button 
                      onClick={() => handleConsultar(service)}
                      className="w-full bg-blue-600 hover:bg-blue-700 text-white rounded-full font-mono text-xs tracking-wider uppercase flex items-center justify-center gap-2"
                    >
                      <MessageCircle className="w-4 h-4" />
                      Consultar
                    </Button>
                  </CardFooter>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Why Have a Website Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="mt-16"
        >
          <div className="bg-gradient-to-r from-blue-900/20 via-blue-600/20 to-blue-900/20 border border-blue-500/30 rounded-2xl p-8">
            <div className="text-center mb-8">
              <TrendingUp className="w-12 h-12 text-blue-400 mx-auto mb-4" />
              <h3 className="text-2xl font-bold text-white mb-3">¿Por qué es importante tener presencia online?</h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Globe className="w-8 h-8 text-blue-400" />
                </div>
                <h4 className="text-white font-semibold mb-2">Visibilidad 24/7</h4>
                <p className="text-gray-400 text-sm">Tu negocio disponible para clientes en cualquier momento y lugar del mundo.</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Search className="w-8 h-8 text-blue-400" />
                </div>
                <h4 className="text-white font-semibold mb-2">Te encuentran en Google</h4>
                <p className="text-gray-400 text-sm">El 90% de las búsquedas de productos y servicios empiezan en Google.</p>
              </div>
              
              <div className="text-center p-4">
                <div className="w-16 h-16 bg-blue-600/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <ShoppingCart className="w-8 h-8 text-blue-400" />
                </div>
                <h4 className="text-white font-semibold mb-2">Vendé Online</h4>
                <p className="text-gray-400 text-sm">Un catálogo online articulado con compra te permite vender sin límites geográficos.</p>
              </div>
            </div>
            
            <div className="text-center">
              <Button 
                onClick={() => {
                  const message = "Hola! Quiero información sobre el desarrollo de mi página web. ¿Podrían asesorarme?";
                  const whatsappUrl = `https://wa.me/${contactInfo.social.whatsapp}?text=${encodeURIComponent(message)}`;
                  window.open(whatsappUrl, '_blank');
                }}
                className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold text-base inline-flex items-center gap-2"
              >
                <MessageCircle className="w-5 h-5" />
                Quiero mi página web
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default WebServicesSection;
